import { api } from "@/api/axios.ts";
import { ApiIdObject, ApiOperationResult } from "@/types/common.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";
import { ApiFormEdit } from "@/types/forms/form-edit.types.ts";
import { ApiForm, ApiFormView } from "@/types/forms/form-view.types.ts";
import { ApiFormResultAnswer } from "@/types/forms/form.types.ts";
import {
  ApiCandidateFeedbacksOverview,
  ApiFeedbackScoreCard,
  ApiScoreCardLong,
  ApiScoreCardPayload,
  ApiScoreCardSearchFilter,
  ApiScoreCardShort,
} from "@/types/recruit/scorecard.types.ts";

export const apiScoreCard = {
  getById: async function (id: number) {
    return api.get<ApiScoreCardLong>(`/api/v1/scorecards/${id}`);
  },
  search: async (filter: ApiScoreCardSearchFilter, signal?: AbortSignal) => {
    return api.post<ApiPagedData<ApiScoreCardShort>>("/api/v1/scorecards/search", filter, signal);
  },
  create: async (payload?: ApiScoreCardPayload) => {
    return api.post<ApiScoreCardShort>("/api/v1/scorecards", payload ?? {});
  },
  edit: async (id: number, payload: ApiScoreCardPayload) => {
    return api.put<ApiScoreCardShort>(`/api/v1/scorecards/${id}`, payload);
  },
  delete: async (id: number) => {
    return api.delete<ApiOperationResult>(`/api/v1/scorecards/${id}`);
  },
  duplicate: async (id: number) => {
    return api.post<ApiIdObject>(`/api/v1/scorecards/${id}/duplicate`, {});
  },
  getFromById: async (id: number) => {
    return api.get<ApiForm>(`/api/v1/scorecards/${id}/form`);
  },
  formUpdate: async function (id: number, formPayload: ApiFormEdit) {
    return api.put<ApiOperationResult>(`/api/v1/scorecards/${id}/form`, formPayload);
  },
  publish: async function (id: number) {
    return api.post<ApiOperationResult>(`/api/v1/scorecards/${id}/publish`, {});
  },
  requestFeedback: async function (candidateId: number, scorecardId: number) {
    return api.post<ApiFeedbackScoreCard>(`/api/v1/candidates/${candidateId}/feedbacks`, {
      scorecard_id: scorecardId,
    });
  },
  candidateFeedbacksOverview: async function (candidateId: number) {
    return api.get<ApiCandidateFeedbacksOverview>(
      `/api/v1/candidates/${candidateId}/feedbacks/overview`
    );
  },
  addParticipants: async function (
    candidateId: number,
    scorecardId: number,
    employeeIds: number[]
  ) {
    return api.post<ApiOperationResult>(
      `/api/v1/candidates/${candidateId}/feedbacks/participants`,
      {
        scorecard_id: scorecardId,
        employee_ids: employeeIds,
      }
    );
  },
  deleteParticipants: async function (
    candidateId: number,
    scorecardId: number,
    participantId: number
  ) {
    return api.delete<ApiOperationResult>(
      `/api/v1/candidates/${candidateId}/scorecards/${scorecardId}/participants/${participantId}`
    );
  },
  archiveFeedback: async function (candidateId: number, feedbackId: number) {
    return api.delete<ApiOperationResult>(
      `/api/v1/candidates/${candidateId}/feedbacks/${feedbackId}`
    );
  },
  resendInviteFeedback: async function (candidateId: number, feedbackId: number) {
    return api.post<ApiOperationResult>(
      `/api/v1/candidates/${candidateId}/feedbacks/${feedbackId}/resend`,
      {}
    );
  },
  getFeedback: async function (employeeId: number, feedbackId: number) {
    return api.get<ApiFeedbackScoreCard>(`/api/v1/employees/${employeeId}/feedbacks/${feedbackId}`);
  },
  getFormByFeedback: async function (employeeId: number, feedbackId: number) {
    return api.get<ApiFormView>(`/api/v1/employees/${employeeId}/feedbacks/${feedbackId}/form`);
  },
  getResultsForFeedback: async function (employeeId: number, feedbackId: number) {
    return api.get<ApiFormView>(`/api/v1/employees/${employeeId}/feedbacks/${feedbackId}/results`);
  },
  downloadFeedback: async function (employeeId: number, feedbackId: number) {
    return api.get<BlobPart>(
      `/api/v1/employees/${employeeId}/feedbacks/${feedbackId}/download`,
      undefined,
      undefined,
      {
        responseType: "blob",
      }
    );
  },
  saveDraft: async function (employeeId: number, feedbackId: number, data: ApiFormResultAnswer[]) {
    return api.put<ApiOperationResult>(
      `/api/v1/employees/${employeeId}/feedbacks/${feedbackId}`,
      data
    );
  },
  submitResults: async function (
    employeeId: number,
    feedbackId: number,
    data: ApiFormResultAnswer[]
  ) {
    return api.post<ApiOperationResult>(
      `/api/v1/employees/${employeeId}/feedbacks/${feedbackId}`,
      data
    );
  },
};
